import type {
  AppFixedInfo,
  AppVersion,
  DisplayInfo,
  Localization,
  SpaceType,
} from '@/types'
import { resolveOwnerSpaceId } from '@/lib/currentOrg'
import { DEFAULT_CONTENT_LANGUAGE } from '../languages'
import { trimDisplayUrl } from './httpsUrl'

/** 抽屉可编辑表单模型（不含只读的包信息） */
export interface VersionForm {
  changelog: Record<string, string>
  app_fixed_info: AppFixedInfo
  display_info: DisplayInfo
  releaseImmediate: boolean
  scheduled_at: string | null
}

function emptyDisplayInfo(): DisplayInfo {
  return {
    supported_languages: [DEFAULT_CONTENT_LANGUAGE],
    default_language: DEFAULT_CONTENT_LANGUAGE,
    categories: [],
    tags: [],
    localizations: {
      [DEFAULT_CONTENT_LANGUAGE]: {
        tagline: '',
        description: '',
        pc_screenshots: [],
        mobile_screenshots: [],
      },
    },
  }
}

function emptyAppFixedInfo(): AppFixedInfo {
  return {
    distribution_type: 'self_built',
    listing_space_types: ['workspace', 'personal'],
    owner_workspace: undefined,
    owner_space_id: undefined,
    available_type: 'all',
    eligible_members: [],
    eligible_departments: [],
  }
}

/** 创建应用时把入口 / 当前空间写入固定信息；lock 时锁定「空间自建」 */
export function applyCreateSpaceContext(
  info: AppFixedInfo | undefined,
  opts: {
    space_id?: string | null
    lockSpaceContext?: boolean
    space_type?: SpaceType | null
  },
): AppFixedInfo {
  const base = info ? { ...info } : emptyAppFixedInfo()
  // 没有空间 id 时不写 owner_space_id
  const space_id = resolveOwnerSpaceId(opts.space_id)
  if (!space_id) return base

  if (opts.lockSpaceContext) {
    return {
      ...base,
      distribution_type: 'self_built',
      owner_space_id: space_id,
      distribution_locked: true,
    }
  }

  return {
    ...base,
    owner_space_id: space_id,
  }
}

/**
 * 历史数据多为「全部空间可用」、没有 space。
 * 切到「空间自建」且所属空间为空时，写入当前空间。
 * 已有真实 owner_space_id 时不覆盖。
 */
export function fillSelfBuiltOwnerSpace(
  info: AppFixedInfo,
  current: { space_id?: string | null; space_name?: string | null },
): AppFixedInfo {
  if (info.distribution_type !== 'self_built') return info
  if (resolveOwnerSpaceId(info.owner_space_id)) return info
  const space_id = resolveOwnerSpaceId(current.space_id)
  if (!space_id) return info
  const name = current.space_name?.trim()
  return {
    ...info,
    owner_space_id: space_id,
    ...(name ? { owner_workspace: name } : {}),
  }
}

/**
 * 应用固定信息是应用级的，按「是否已有版本审核通过」（distribution_locked）锁定，
 * 不看 is_first_version：首版未过审时新建的 v2 也要能改。
 */
export function canEditAppFixedInfo(
  versionEditable: boolean,
  info: AppFixedInfo | undefined,
): boolean {
  return versionEditable && !info?.distribution_locked
}

/** 拉远端详情时保留本地已写入的入口空间上下文 */
export function mergeAppFixedInfo(
  remote: AppFixedInfo | undefined,
  local: AppFixedInfo | undefined,
): AppFixedInfo | undefined {
  if (!local) return remote
  if (!remote) return local
  if (local.distribution_locked) return { ...remote, ...local }
  if (local.owner_space_id && !remote.owner_space_id) {
    return {
      ...remote,
      owner_space_id: local.owner_space_id,
      owner_workspace: remote.owner_workspace ?? local.owner_workspace,
    }
  }
  return remote
}

const EMPTY_LOC: Localization = {
  tagline: '',
  description: '',
  pc_screenshots: [],
  mobile_screenshots: [],
}

/** 按字段合并：远端非空优先，否则保留本地（文案与截图各自独立） */
function mergeLocalization(
  remote: Localization | undefined,
  local: Localization | undefined,
): Localization {
  if (!remote) return local ?? EMPTY_LOC
  if (!local) return remote
  return {
    tagline: remote.tagline.trim() ? remote.tagline : local.tagline,
    description: remote.description.trim()
      ? remote.description
      : local.description,
    pc_screenshots: remote.pc_screenshots.length
      ? remote.pc_screenshots
      : local.pc_screenshots,
    mobile_screenshots: remote.mobile_screenshots.length
      ? remote.mobile_screenshots
      : local.mobile_screenshots,
  }
}

/**
 * 拉详情时合并展示信息：远端某语言文案/截图为空时保留本地已填内容，
 * 避免列表/详情缺 localizations 时把刚保存的简介/描述冲掉。
 * categories / tags 始终以远端为准（含空数组=已清空）。
 */
export function mergeDisplayInfo(
  remote: DisplayInfo | undefined,
  local: DisplayInfo | undefined,
): DisplayInfo | undefined {
  if (!remote) return local
  if (!local) return remote

  const langs = new Set([
    ...Object.keys(remote.localizations),
    ...Object.keys(local.localizations),
    ...remote.supported_languages,
    ...local.supported_languages,
  ])
  const localizations: DisplayInfo['localizations'] = {}
  for (const lang of langs) {
    localizations[lang] = mergeLocalization(
      remote.localizations[lang],
      local.localizations[lang],
    )
  }

  return {
    ...local,
    ...remote,
    // 分类/标签信远端（含 []）；语言列表远端为空时才回退本地
    supported_languages: remote.supported_languages.length
      ? remote.supported_languages
      : local.supported_languages,
    default_language: remote.default_language || local.default_language,
    localizations,
  }
}

function normalizeAppFixedInfo(info: AppFixedInfo): AppFixedInfo {
  return {
    ...info,
    eligible_members: info.eligible_members ?? [],
    eligible_departments: info.eligible_departments ?? [],
    available_type:
      info.available_type ??
      ((info.eligible_members?.length ?? 0) > 0 ||
      (info.eligible_departments?.length ?? 0) > 0
        ? 'specified'
        : 'all'),
  }
}

export function formFromVersion(v: AppVersion): VersionForm {
  return {
    changelog: { ...(v.changelog ?? {}) },
    app_fixed_info: v.app_fixed_info
      ? normalizeAppFixedInfo(JSON.parse(JSON.stringify(v.app_fixed_info)))
      : emptyAppFixedInfo(),
    display_info: v.display_info
      ? JSON.parse(JSON.stringify(v.display_info))
      : emptyDisplayInfo(),
    releaseImmediate: v.scheduled_at == null,
    scheduled_at: v.scheduled_at,
  }
}

export function versionPatchFromForm(
  form: VersionForm,
  opts?: { is_first_version?: boolean },
): Partial<AppVersion> {
  const di = form.display_info
  const patch: Partial<AppVersion> = {
    app_fixed_info: form.app_fixed_info,
    display_info: {
      ...di,
      website_url: trimDisplayUrl(di.website_url),
      privacy_policy_url: trimDisplayUrl(di.privacy_policy_url),
      terms_url: trimDisplayUrl(di.terms_url),
    },
    scheduled_at: form.releaseImmediate ? null : form.scheduled_at,
  }

  // 首版不传 changelog；非首版也只传有内容的语言条目，避免空对象 {}
  if (!opts?.is_first_version) {
    const changelog = Object.fromEntries(
      Object.entries(form.changelog).filter(([, text]) => text.trim()),
    )
    if (Object.keys(changelog).length > 0) {
      patch.changelog = changelog
    }
  }

  return patch
}

export function isFormDirty(form: VersionForm, v: AppVersion): boolean {
  const opts = { is_first_version: !!v.is_first_version }
  const a = JSON.stringify(versionPatchFromForm(form, opts))
  const b = JSON.stringify(versionPatchFromForm(formFromVersion(v), opts))
  return a !== b
}

/** 确保某内容语言存在本地化对象 */
export function ensureLocalization(info: DisplayInfo, lang: string): DisplayInfo {
  if (info.localizations[lang]) return info
  return {
    ...info,
    localizations: {
      ...info.localizations,
      [lang]: {
        tagline: '',
        description: '',
        pc_screenshots: [],
        mobile_screenshots: [],
      },
    },
  }
}
